/* eslint-disable react/no-unescaped-entities */
import { PropTypes } from 'prop-types';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Tooltip, FormControl, FormControlLabel, Switch, Dialog, DialogTitle, Button, IconButton, Card, Avatar, CardHeader, Box, DialogContent, DialogActions, Checkbox, Grid } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Error as ErrorIcon } from '@mui/icons-material';
import { setAlertMessage } from '../features/layoutSlice';
import { useDestroyOrganizationUserMutation, useEditOneOrganizationUserMutation, useSetOwnerMutation } from '../features/api/rest/apiOrganizationsSlice';
import { useGetProfileQuery } from '../features/api/rest/apiProfileSlice';

function UserCard({ username = 'Name not available', isadmin = false, isowner = false, id = null, duser = null, hasPermission = false, adminCount = 0 }) {
  const { opk } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [deleteuser] = useDestroyOrganizationUserMutation();
  const [editoneuser] = useEditOneOrganizationUserMutation();
  const [setOwner] = useSetOwnerMutation();

  const { currentData: profile } = useGetProfileQuery();

  const [isadminedit, setisadminedit] = useState(isadmin);
  const [isOwnerEdit, setIsOwnerEdit] = useState(isowner);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const deleteUserHandler = () => {
    if (profile.id === duser) dispatch(setAlertMessage({ text: t('organizations.user.alertdelete'), type: 'error' }));
    else deleteuser({ opk, id });
    setIsDeleteDialogOpen(false);
  };

  const openEditDialog = () => {
    if (isadmin && adminCount === 1) dispatch(setAlertMessage({ text: t('organizations.user.alertedit'), type: 'error' }));
    else setIsEditDialogOpen(true);
  };
  const editUserHandler = async () => {
    editoneuser({
      opk,
      id,
      payload: { duser, is_admin: isadminedit, organization: opk },
    });
    if (isOwnerEdit && !isowner) {
      const response = await setOwner({ opk, id });
      if (response?.error) {
        dispatch(setAlertMessage({ text: response.error?.data?.detail, type: 'error' }));
      }
    }
    setIsEditDialogOpen(false);
  };

  const confirmOwnershipTransfer = () => {
    setIsOwnerEdit(true);
    setIsConfirmDialogOpen(false);
  };

  const warningLabel = (
    <Box>
      <IconButton aria-label="delete" size="large" disableRipple sx={{ cursor: 'default' }}>
        <ErrorIcon fontSize="inherit" color="warning" />
      </IconButton>
      {t(`organizations.user.transferOrganization`)}
    </Box>
  );

  return (
    <>
      <Card sx={{ width: '18em' }}>
        <CardHeader
          avatar={<Avatar>{username.charAt(0).toUpperCase()}</Avatar>}
          title={username}
          subheader={isadmin && t('organizations.user.admin')}
          action={
            hasPermission && (
              <Box sx={{ ml: 2 }}>
                <Tooltip title={t('common.actions.edit')} placement="right">
                  <IconButton onClick={openEditDialog}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                {!isadmin && (
                  <Tooltip title={t('common.actions.delete')} placement="right">
                    <IconButton onClick={() => setIsDeleteDialogOpen(true)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )
          }
        />
      </Card>
      <Dialog onClose={() => setIsDeleteDialogOpen(false)} open={isDeleteDialogOpen}>
        <DialogTitle>{t('organizations.user.delete_user', { user: username })}</DialogTitle>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={() => setIsDeleteDialogOpen(false)}>
            {t('organizations.form.no')}
          </Button>
          <Button variant="contained" color="secondary" onClick={deleteUserHandler}>
            {t('organizations.form.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog onClose={() => setIsEditDialogOpen(false)} open={isEditDialogOpen} sx={{ textAlign: 'center' }}>
        <DialogTitle>{t('organizations.user.edit_user', { user: username })}</DialogTitle>
        <DialogContent>
          <FormControl>
            <FormControlLabel sx={{ alignSelf: 'center' }} control={<Switch checked={isadminedit} onChange={() => setisadminedit(!isadminedit)} />} label={t(`organizations.user.admin`)} labelPlacement="start" />
            <FormControlLabel
              control={<Checkbox checked={isOwnerEdit} onChange={() => (isOwnerEdit ? setIsOwnerEdit(false) : setIsConfirmDialogOpen(true))} />}
              label={warningLabel}
              labelPlacement="start"
              title={`${t(`common.actions.warning`)}! ${t(`organizations.user.ownerWarning`)}`}
              disabled={(isOwnerEdit && isowner) || (!isowner && profile.id === duser)}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setIsOwnerEdit(false);
                }}
              >
                {t('organizations.form.close')}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button variant="contained" color="secondary" onClick={editUserHandler}>
                {t('organizations.user.confirm')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
      <Dialog onClose={() => setIsConfirmDialogOpen(false)} open={isConfirmDialogOpen} sx={{ textAlign: 'center' }}>
        <DialogTitle sx={{ textTransform: 'uppercase', color: 'red' }}>
          <IconButton aria-label="delete" size="large" disableRipple sx={{ cursor: 'default' }}>
            <ErrorIcon fontSize="inherit" color="warning" />
          </IconButton>
          {t('common.actions.warning')} ! {t('common.actions.irreversibleAction')} !
          <IconButton aria-label="delete" size="large" disableRipple sx={{ cursor: 'default' }}>
            <ErrorIcon fontSize="inherit" color="warning" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Trans t={t} i18nKey="organizations.user.ownerWarningDialog">
            Il trasferimento di un'organizzazione è <strong>un'operazione irreversibile</strong>. Una volta trasferita l'organizzazione, non ne sarai più il proprietario.
          </Trans>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6}>
              <Button variant="contained" color="primary" onClick={() => setIsConfirmDialogOpen(false)}>
                {t('common.actions.cancel')}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button variant="contained" color="secondary" onClick={confirmOwnershipTransfer}>
                {t('common.actions.confirm')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </>
  );
}

UserCard.propTypes = {
  id: PropTypes.number,
  duser: PropTypes.number,
  username: PropTypes.string,
  isadmin: PropTypes.bool,
  isowner: PropTypes.bool,
  adminCount: PropTypes.number,
  hasPermission: PropTypes.bool,
};
export default UserCard;
